import { animate, style, transition, trigger } from "@angular/animations";
import { Component, Input, OnDestroy, OnInit } from "@angular/core";
import { StateService, UIRouterGlobals } from "@uirouter/core";
import { debounce } from "lodash";
import { finalize, tap } from "rxjs/operators";
import { ChartHelperService } from "../chart/chart-helper.service";
import { ErrorMessageWithoutTypeService } from "../error-message-without-type/error-message-without-type.service";
import { EventService } from "../event-service/event.service";
import { Services } from "../integration/services";
import { IssueIndicatorsService } from "../issue-indicators/issue-indicators.service";
import { KeyboardService } from "../keyboard/keyboard.service";
import { GroupService } from "../models/group.service";
import { StatsmanService } from "../models/statsman.service";

@Component({
  selector: "ab-group-list",
  templateUrl: "./group-list.component.html",
  animations: [
    trigger("groupAnimation", [
      transition(":enter", [
        style({ opacity: 0 }),
        animate("400ms", style({ opacity: 1 })),
      ]),
      transition(":leave", [
        style({ opacity: 1 }),
        animate("400ms", style({ opacity: 0 })),
      ]),
    ]),
  ],
})
export class GroupListComponent implements OnDestroy, OnInit {
  @Input() public filters: any;
  @Input() public project: any;

  public activeGroupIndex: number;
  public aggregationsCalloutGroupId: string;
  public commentCreatedSub: any;
  public commentDeletedSub: any;
  public currentPage: number;
  public cursors: any[];
  public deleteActiveGroupSub: any;
  public groupMutedSub: any;
  public groupResolvedSub: any;
  public groups: any;
  public groupsActivateSub: any;
  public groupsFilterSub: any;
  public groupsListPartial: string;
  public groupUnmutedSub: any;
  public isHovered: boolean;
  public issueCreatedSub: any;
  public issueDeletedSub: any;
  public loading: boolean;
  public pageParams: any;
  public searchDataLoadedSub: any;
  public sortOptions: { label: string; value: string }[];
  public totalItems: number;
  public totalPages: number;
  public unregisterKeydown: any;

  private PAGESIZE = 20;

  // We debounce state transitions to make rapid-fire keyboard
  // navigation more responsive, since state changes completely
  // rerender large portions of the document.
  private goToGroupState = debounce((group: any) => {
    const params = this.groupParams(group.id);

    group.loading = true;
    this.$state
      .go("groups-show", params)
      .finally(() => (group.loading = false));
  }, 100);

  constructor(
    private $state: StateService,
    private $uiRouterGlobals: UIRouterGlobals,
    private chartHelperService: ChartHelperService,
    private errorMessageWithoutTypeService: ErrorMessageWithoutTypeService,
    private groupService: GroupService,
    private issueIndicatorsService: IssueIndicatorsService,
    private keyboardService: KeyboardService,
    private statsmanService: StatsmanService,
    public eventService: EventService,
  ) {}

  public loadOccurrencesCharts() {
    const names = [];
    for (const g of this.groups) {
      names.push(`groups.${g.id}.total_notices`);
    }

    const params = {
      projectId: this.project.id,
      name: names,
      type: "counter",
      dur: "90d/1d",
      last_n: 10,
    };
    return this.statsmanService.query(params).subscribe((data: any) => {
      this.assignOccurrencesCharts(data);
    });
  }

  public reloadPage(opts?: any) {
    this.loading = true;

    if (this.pageParams.resolved === "any") {
      delete this.pageParams.resolved;
    }

    let observable = this.groupService.query(this.pageParams).pipe(
      tap(this.displayPage.bind(this)),
      finalize(() => {
        this.groupsListPartial = this.getGroupsListPartial();
        this.loading = false;
      }),
    );

    if (opts != null ? opts.highlight : undefined) {
      observable = observable.pipe(tap(this.highlightChanges.bind(this)));
    }

    return observable;
  }

  public openUrl(url: string) {
    window.open(url, "_blank");
    return true;
  }

  public groupParams(groupId: number) {
    return {
      projectId: this.project.id,
      groupId,
      tab: "overview",
    };
  }

  public hasPrevPage() {
    return this.cursors.length >= 2;
  }

  public hasNextPage() {
    return this.lastCursor() !== "";
  }

  // Loads & renders the next page of groups.
  public loadNextPage() {
    return this.loadPage({ start: this.lastCursor() });
  }

  // Loads & renders the previous page of groups.
  public loadPrevPage() {
    this.cursors = this.cursors.slice(0, -2);
    this.loadPage({ start: this.lastCursor() });
  }

  // This is used to show the first number of the "41-60 of 432" in the group
  // list controls
  public currentPageStart() {
    return this.PAGESIZE * (this.currentPage - 1) + 1;
  }

  // This is used to show the second number of the "41-60 of 432" in the group
  // list controls
  public currentPageEnd() {
    return this.currentPageStart() + (this.groups.length - 1);
  }

  // Used as the ng-change handler in the view to toggle all groups on the page
  // Only triggered when the user checks/unchecks, not when we change its state.
  public toggleSelectionForAll() {
    const shouldSelect = this.selectedGroups().length < 1;
    return this.groups.map((group: any) => (group.selected = shouldSelect));
  }

  public resolveSelected() {
    const selection = this.selectedGroups();
    if (selection.length > 0) {
      return this.resolveGroups(selection);
    } else if (this.groups.length > 0) {
      return this.resolveGroups([this.groups[this.activeGroupIndex]]);
    }
  }

  public unresolveSelected() {
    const selection = this.selectedGroups();
    if (selection.length > 0) {
      return this.unresolveGroups(selection);
    } else if (this.groups.length > 0) {
      return this.unresolveGroups([this.groups[this.activeGroupIndex]]);
    }
  }

  public selectedGroups() {
    return this.groups.filter((g: any) => g.selected);
  }

  public stopPropagation(event: Event) {
    event.stopPropagation();
  }

  public activateGroup(index: number, $event?: any) {
    // Prevent double changing of the state through both ui-sref and ng-click,
    // which results in two identical API requests.
    if ($event) {
      $event.preventDefault();
    }

    // Preserve the ability to open group in a new tab with help of:
    //   * Ctrl + Click (Windows & Linux)
    //   * Meta + Click (OSX)
    //   * Middle click (multiplatform)
    if ($event && ($event.ctrlKey || $event.metaKey || $event.button === 1)) {
      const url = this.$state.href("groups-show", {
        projectId: this.groups[index].projectId,
        groupId: this.groups[index].id,
      });
      window.open(url, "_blank");
      return;
    }

    this.activeGroupIndex = index;
    const group = this.groups[this.activeGroupIndex];
    this.resetGroupState(group);
    window.document.title = this.groupTitle(group);
    this.goToGroupState(group);
  }

  public updateSort(sortOption: any) {
    this.filters.order = sortOption.value;
  }

  public ngOnDestroy() {
    this.unregisterKeydown();
    this.groupsFilterSub.unsubscribe();
    this.groupResolvedSub.unsubscribe();
    this.groupMutedSub.unsubscribe();
    this.groupUnmutedSub.unsubscribe();
    this.groupsActivateSub.unsubscribe();
    this.commentCreatedSub.unsubscribe();
    this.commentDeletedSub.unsubscribe();
    this.issueCreatedSub.unsubscribe();
    this.issueDeletedSub.unsubscribe();
    this.searchDataLoadedSub.unsubscribe();
    this.deleteActiveGroupSub.unsubscribe();
  }

  public ngOnInit() {
    this.groups = [];
    this.activeGroupIndex = -1;
    // TODO: these counters need to update as groups are resolved
    this.totalItems = 0; // Number of items with the current filter settings
    this.totalPages = 0; // Number of pages with the current filter settings
    this.currentPage = 1;
    this.cursors = [];
    this.issueIndicatorsService.clearCachedIssues();

    this.sortOptions = [
      { label: "Last occurrence", value: "last_notice" },
      { label: "Occurrence count", value: "notice_count" },
      { label: "Creation date", value: "created" },
      { label: "Error weight", value: "weight" },
      { label: "Error severity", value: "severity" },
    ];

    // Handle keyboard navigation with keydown events rather than keypress events.
    // This is because arrow keys do not fire keypress events, and it's nice to use
    // a single event type for all keyboard nav.
    // NB: Uses the keyboard service to allow us to capture keyboard events when
    // the user/browser focus is outside the group list.
    this.unregisterKeydown = this.keyboardService.onKeydown((event: any) => {
      // Don't trigger on CMD+/CTRL+ key combinations e.g. CMD+R to reload.
      if (event.metaKey || event.ctrlKey) {
        return;
      }

      switch (event.keyCode) {
        // 74=j
        case 74:
          this.activateNextGroup();
          break;
        // 40=down-arrow
        case 40:
          this.activateNextGroup();
          break;
        // 75=k
        case 75:
          this.activatePrevGroup();
          break;
        // 38=up-arrow
        case 38:
          this.activatePrevGroup();
          break;
        // 88=x
        case 88:
          this.toggleActiveGroupSelection();
          break;
        // 82=r
        case 82:
          if (!event.shiftKey) {
            return;
          }
          this.toggleResolvedOfSelected();
          break;
        // 77=m
        case 77:
          if (!event.shiftKey) {
            return;
          }
          this.toggleMutedOfSelected();
          break;
        default:
          return;
      }
      return event.preventDefault();
    });

    // The abGroupFilter component publishes this when the group filters change
    this.groupsFilterSub = this.eventService.subscribe("groups:filter", () => {
      this.cursors = [];
      this.loadPage();
      this.hideUndoNotification();
    });

    // The Group model publishes these when (un-)resolving
    this.groupResolvedSub = this.eventService.subscribe(
      "group:resolved",
      debounce((group: any) => {
        for (const g of this.groups) {
          if (g.id === group.id) {
            g.resolved = group.resolved;
            break;
          }
        }

        this.reloadPage().subscribe();
      }, 50),
    );

    this.groupMutedSub = this.eventService.subscribe(
      "group:muted",
      this.onGroupMuteChange.bind(this),
    );

    this.groupUnmutedSub = this.eventService.subscribe(
      "group:unmuted",
      this.onGroupMuteChange.bind(this),
    );

    this.groupsActivateSub = this.eventService.subscribe(
      "groups:activate",
      this.setActiveGroupIndex.bind(this),
    );

    this.commentCreatedSub = this.eventService.subscribe(
      "comment:created",
      (groupId: any) => {
        for (const g of this.groups) {
          if (g.id === groupId) {
            g.commentCount = g.commentCount + 1;
            break;
          }
        }
      },
    );

    this.commentDeletedSub = this.eventService.subscribe(
      "comment:deleted",
      (groupId: any) => {
        for (const g of this.groups) {
          if (g.id === groupId) {
            g.commentCount = g.commentCount - 1;
            break;
          }
        }
      },
    );

    this.issueCreatedSub = this.eventService.subscribe(
      "issue:created",
      (data: any) => {
        for (const group of this.groups) {
          if (group.id === data.issue.groupId) {
            this.issueIndicatorsService.addIndicatorOnGroup(
              group,
              data.serviceName,
              data.issue,
            );
            break;
          }
        }
      },
    );

    this.issueDeletedSub = this.eventService.subscribe(
      "issue:deleted",
      (data: any) => {
        for (const group of this.groups) {
          if (group.id === data.groupId) {
            this.issueIndicatorsService.removeIndicatorFromGroup(
              group,
              data.serviceName,
            );
            break;
          }
        }
      },
    );

    this.searchDataLoadedSub = this.eventService.subscribe(
      "groups:searchDataLoaded",
      this.loadPage.bind(this),
    );

    this.deleteActiveGroupSub = this.eventService.subscribe(
      "groups:deleteActiveGroup",
      () => {
        let index = this.activeGroupIndex;
        // This group isn't in the currently displayed list. Activate the
        // first group in the list and leave the list unchanged.
        if (index < 0) {
          return this.activateGroup(0);
        }

        this.groups.splice(this.activeGroupIndex, 1);

        if (this.groups.length === 0) {
          this.groupsListPartial = this.getGroupsListPartial();
          this.$state.go("project-errors", { projectId: this.project.id });
          return;
        }

        if (index >= this.groups.length) {
          index = 0;
        }
        return this.activateGroup(index);
      },
    );
  }

  public trackById(_index: number, item: any) {
    return item.id;
  }

  public trackByIndex(index: number, _item: any) {
    return index;
  }

  private getAggregationsCalloutGroupId() {
    const group = this.groups.find((g: any) => g.noticeCount > 20);
    return group?.id;
  }

  private assignOccurrencesCharts(data: any) {
    this.groups.forEach((group: any) => {
      const groupData = data[`groups.${group.id}.total_notices`];
      if (!groupData) {
        return;
      }

      group.occurrencesChart = {
        data: {
          labels: groupData.time,
          datasets: [
            {
              label: "Errors",
              data: groupData.count,
              backgroundColor: "#b8b8b8", // ab-grey-700
              borderColor: "#b8b8b8", // ab-grey-700
              hoverBackgroundColor: "#646464", // ab-grey-400
              hoverBorderColor: "#646464", // ab-grey-400
              minBarLength: 1,
              barPercentage: 0.85,
              categoryPercentage: 1,
            },
          ],
        },
        options: this.chartHelperService.defaultMiniBarChartOptions(),
      };
    });
  }

  private highlightChanges(pageData: any) {
    if (pageData.oldGroups == null) {
      return;
    }
    const { oldGroups } = pageData;
    if (oldGroups.length === 0 || this.currentPage !== pageData.page) {
      return pageData;
    }

    const groupIds = oldGroups.map((g: any) => g.id);
    for (const g of pageData.groups) {
      this.resetGroupState(g);

      const i = groupIds.indexOf(g.id);

      if (i === -1) {
        g.isNew = true;
        continue;
      }

      const diff = g.noticeCount - oldGroups[i].noticeCount;
      if (diff > 0) {
        g.newNoticeCount = diff;
      }
    }

    return pageData;
  }

  private setActiveGroupIndex(groupId: any) {
    for (let index = 0; index < this.groups.length; index++) {
      const g = this.groups[index];
      if (parseInt(g.id, 10) === parseInt(groupId, 10)) {
        this.activeGroupIndex = index;
        return;
      }
    }

    return (this.activeGroupIndex = -1);
  }

  private displayPage(pageData: any) {
    this.totalItems = pageData.count;
    pageData.oldGroups = this.groups;
    this.groups = pageData.groups;
    this.totalPages = Math.ceil(pageData.count / this.PAGESIZE);
    this.currentPage = pageData.page;

    if (this.groups.length === 0 && this.pageParams.start) {
      delete this.pageParams.start;
      this.cursors = [];
      this.reloadPage().subscribe((newPageData: any) =>
        this.cursors.push(newPageData.end),
      );
    }

    this.addIssueIndicatorsOnGroups();
    this.setActiveGroupIndex(this.$uiRouterGlobals.params.groupId);
    this.loadOccurrencesCharts();
    this.aggregationsCalloutGroupId = this.getAggregationsCalloutGroupId();

    return pageData;
  }

  private loadPage(params?: any) {
    this.pageParams = Object.assign(
      { limit: this.PAGESIZE },
      this.filters,
      params,
    );
    return this.reloadPage().subscribe((pageData: any) =>
      this.cursors.push(pageData.end),
    );
  }

  private resolveGroups(groups: any) {
    return this.groupService.resolveMultiple(groups).subscribe(() => {
      this.registerUndoAction(groups);
    });
  }

  private unresolveGroups(groups: any) {
    this.groupService.unresolveMultiple(groups).subscribe(() => {
      this.registerUndoAction(groups);
    });
  }

  // Notifies the undo ctrl that a new undo-able action is available. Provides
  // the message to display, and an undo callback function to perform the undo.
  private registerUndoAction(groups: any) {
    let undoAction;
    const phrase = groups.length === 1 ? "error was" : "errors were";
    if (groups[0].resolved) {
      undoAction = {
        message: `${groups.length} ${phrase} resolved.`,
        undoObservable: this.groupService.unresolveMultiple(groups),
      };
    } else {
      undoAction = {
        message: `${groups.length} ${phrase} unresolved.`,
        undoObservable: this.groupService.resolveMultiple(groups),
      };
    }
    this.eventService.publish("undo:registerAction", undoAction);
  }

  // Notifies the undo ctrl to hide the undo notification/flash.
  private hideUndoNotification() {
    this.eventService.publish("undo:hide");
  }

  // Toggles the selection state of the active group. Used by keyboard nav.
  private toggleActiveGroupSelection() {
    const activeGroup = this.groups[this.activeGroupIndex];
    if (!activeGroup) {
      return;
    }
    activeGroup.selected = !activeGroup.selected;
  }

  // Toggles the resolved state of the selected groups. Used by keyboard nav.
  private toggleResolvedOfSelected() {
    const states = this.groups
      .filter((g: any) => g.selected)
      .map((g: any) => g.resolved);
    if (states.indexOf(false) > -1) {
      return this.resolveSelected();
    } else if (states.indexOf(true) > -1) {
      return this.unresolveSelected();
    }
  }

  // Toggles the muted state of the selected groups. Used by keyboard nav.
  private toggleMutedOfSelected() {
    const groups = this.selectedGroups();
    if (!(groups.length > 0)) {
      return;
    }
    if (groups.some((group: any) => group.muted === false)) {
      return this.groupService.muteMultiple(groups).subscribe();
    } else {
      return this.groupService.unmuteMultiple(groups).subscribe();
    }
  }

  private addIssueIndicatorsOnGroups() {
    if (this.project.integrations == null) {
      return;
    }

    for (const group of this.groups) {
      group.issueIndicators = [];

      for (let serviceName of Object.keys(this.project.integrations)) {
        if (Services.ISSUE.indexOf(serviceName) > -1) {
          serviceName =
            serviceName === "pivotalTracker" ? "pivotal" : serviceName;
          this.issueIndicatorsService.addIndicatorOnGroup(group, serviceName);
        }
      }
    }
  }

  private activateNextGroup() {
    if (this.activeGroupIndex < this.groups.length - 1) {
      return this.activateGroup(this.activeGroupIndex + 1);
    }
  }

  private activatePrevGroup() {
    if (this.activeGroupIndex > 0) {
      return this.activateGroup(this.activeGroupIndex - 1);
    }
  }

  private groupTitle(group: any) {
    const err = group.errors[0];
    return (
      err.type + ": " + this.errorMessageWithoutTypeService.messageFor(err)
    );
  }

  private resetGroupState(group: any) {
    if (group.isNew) {
      delete group.isNew;
    }
    if (group.noticeCount > 0) {
      return delete group.newNoticeCount;
    }
  }

  private hasCustomFilters() {
    let filtering = false;
    for (const key of Object.keys(this.filters || {})) {
      // don't consider filters that are the default state
      const value = this.filters[key];
      if (key === "projectId" || key === "order") {
        continue;
      }
      if (key === "resolved" && value.toString() === "false") {
        continue;
      }
      filtering = true;
      break;
    }

    return filtering;
  }

  private getGroupsListPartial() {
    // there are groups to show, return early and show them
    if (this.groups.length > 0) {
      return "results";
    }

    if (this.hasCustomFilters()) {
      return "no results";
    } else {
      return "all resolved";
    }
  }

  private lastCursor() {
    if (this.cursors.length > 0) {
      return this.cursors[this.cursors.length - 1];
    }
    return "";
  }

  private onGroupMuteChange(group: any) {
    for (const g of this.groups) {
      if (g.id === group.id) {
        g.muted = group.muted;
        break;
      }
    }
  }
}
